/**
 * Session kit push reminders after PWA (or any) check-in.
 *
 * Rules:
 * - Always: notify on check-in
 * - If check-in is a day or more before the session (NZ date): also morning-of + 1 hour prior
 * - If check-in is on the session day (NZ): only on check-in + 1 hour prior (skip morning-of)
 * - Skip any future slot that is already in the past (or within the grace window)
 * - Every title/body includes session weekday, date, and start time
 */

import {
  addDaysIso,
  formatClock12h,
  nzWallClock,
  nzWallDateTimeToUtc,
  timeToMinutes,
} from './time';

export const KIT_REMINDER_KINDS = ['on_check_in', 'morning_of', 'one_hour_prior'] as const;
export type KitReminderKind = (typeof KIT_REMINDER_KINDS)[number];

/** NZ wall-clock time for the “morning of session” push (06:30). */
export const KIT_MORNING_HOUR = 6;
export const KIT_MORNING_MINUTE = 30;

/** Do not schedule a slot if it falls within this many ms of “now”. */
export const KIT_REMINDER_PAST_GRACE_MS = 60_000;

const NZ_LOCALE = 'en-NZ';

export type KitReminderPlanItem = {
  kind: KitReminderKind;
  scheduled_for: Date;
  title: string;
};

export type KitReminderPayload = {
  title: string;
  body: string;
  url: string;
  equipment: string[];
  session_id: string;
  session_date: string;
  start_time: string;
  swimmer_id: string;
  swimmer_name: string | null;
  kind: KitReminderKind;
};

/** e.g. "Thu 14 Aug · 6:00 am" */
export function formatSessionWhenLabel(sessionDate: string, startTime: string): string {
  const d = new Date(`${sessionDate}T12:00:00`);
  const day = d.toLocaleDateString(NZ_LOCALE, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  return `${day} · ${formatClock12h(startTime)}`;
}

/** e.g. "Thursday 14 Aug at 6:00 am" */
export function formatSessionWhenLong(sessionDate: string, startTime: string): string {
  const d = new Date(`${sessionDate}T12:00:00`);
  const day = d.toLocaleDateString(NZ_LOCALE, {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  });
  return `${day} at ${formatClock12h(startTime)}`;
}

/** Which reminder kinds apply given when the check-in happened (NZ dates). */
export function planKitReminderKinds(opts: {
  checkInAt?: Date;
  sessionDate: string;
}): KitReminderKind[] {
  const checkInNz = nzWallClock(opts.checkInAt ?? new Date());
  const kinds: KitReminderKind[] = ['on_check_in'];
  if (checkInNz.isoDate < opts.sessionDate) {
    kinds.push('morning_of');
  }
  kinds.push('one_hour_prior');
  return kinds;
}

export function kitReminderTitle(
  kind: KitReminderKind,
  sessionDate: string,
  startTime: string,
): string {
  const when = formatSessionWhenLabel(sessionDate, startTime);
  if (kind === 'morning_of') return `Today's kit · ${when}`;
  if (kind === 'one_hour_prior') return `Kit in 1 hour · ${when}`;
  return `Session kit · ${when}`;
}

export function formatKitReminderBody(opts: {
  equipment: string[];
  sessionDate: string;
  startTime: string;
  squadName?: string | null;
  swimmerName?: string | null;
}): string {
  const kit =
    opts.equipment.length > 0
      ? opts.equipment.join(' · ')
      : 'No special kit listed — bring usual gear';
  const when = formatSessionWhenLong(opts.sessionDate, opts.startTime);
  const who = [opts.swimmerName?.trim() || null, opts.squadName?.trim() || null]
    .filter(Boolean)
    .join(' · ');
  const header = who ? `${who} — ${when}` : when;
  return `${header}\n${kit}`;
}

/** In-app / toast line after check-in (single paragraph). */
export function formatKitCheckInNotice(opts: {
  equipment: string[];
  sessionDate: string;
  startTime: string;
  squadName?: string | null;
  swimmerName?: string | null;
  hasFollowUpReminders: boolean;
}): string {
  const when = formatSessionWhenLabel(opts.sessionDate, opts.startTime);
  const kit =
    opts.equipment.length > 0
      ? opts.equipment.join(' · ')
      : 'No special kit listed — bring usual gear';
  const who = opts.swimmerName?.trim();
  const squad = opts.squadName?.trim();
  const bits = [who || null, squad || null].filter(Boolean);
  const context = bits.length > 0 ? ` (${bits.join(' · ')})` : '';
  const followUp = opts.hasFollowUpReminders
    ? ' We’ll remind you again before the session.'
    : '';
  return `Session kit · ${when}${context}: ${kit}.${followUp}`;
}

/**
 * Build concrete schedule instants. Drops morning-of when it would be at/after
 * the 1-hour-prior slot, and drops any slot already in the past.
 */
export function buildKitReminderSchedule(opts: {
  checkInAt?: Date;
  sessionDate: string;
  sessionStartTime: string;
}): KitReminderPlanItem[] {
  const now = opts.checkInAt ?? new Date();
  const kinds = planKitReminderKinds({ checkInAt: now, sessionDate: opts.sessionDate });
  const sessionStartUtc = nzWallDateTimeToUtc(opts.sessionDate, opts.sessionStartTime, now);
  const oneHourPrior = new Date(sessionStartUtc.getTime() - 60 * 60_000);
  const morningUtc = nzWallDateTimeToUtc(
    opts.sessionDate,
    `${String(KIT_MORNING_HOUR).padStart(2, '0')}:${String(KIT_MORNING_MINUTE).padStart(2, '0')}:00`,
    now,
  );

  const out: KitReminderPlanItem[] = [];
  for (const kind of kinds) {
    let at: Date;
    if (kind === 'on_check_in') at = now;
    else if (kind === 'morning_of') {
      // Morning after (or equal to) 1h-prior is useless — skip.
      if (morningUtc.getTime() >= oneHourPrior.getTime()) continue;
      at = morningUtc;
    } else {
      at = oneHourPrior;
    }

    if (kind !== 'on_check_in' && at.getTime() <= now.getTime() + KIT_REMINDER_PAST_GRACE_MS) {
      continue;
    }

    out.push({
      kind,
      scheduled_for: at,
      title: kitReminderTitle(kind, opts.sessionDate, opts.sessionStartTime),
    });
  }
  return out;
}

/** True when check-in NZ date is strictly before session NZ date. */
export function isCheckInDayOrMoreBeforeSession(
  sessionDate: string,
  checkInAt: Date = new Date(),
): boolean {
  return nzWallClock(checkInAt).isoDate < sessionDate;
}

/** Minutes from midnight for session start — helper for tests/UI. */
export function sessionStartMinutes(sessionStartTime: string): number {
  return timeToMinutes(sessionStartTime);
}

/** Next NZ calendar day after `isoDate`. */
export function nextNzDate(isoDate: string): string {
  return addDaysIso(isoDate, 1);
}
